import { isApiError } from '@aiptyg/types';
import { post } from '@aiptyg/api-client';
import { buildConfig } from '@aiptyg/config';
import { buildCosThumbnailUrl, type CosThumbnailOptions } from './cosThumbnail';

export type AdminImageUploadModule =
  | 'task-icon'
  | 'task-step'
  | 'home-popup'
  | 'support-comment'
  | 'withdrawal-proof';

interface UploadCredentialResponse {
  bucket: string;
  region: string;
  domain: string;
  objectKey: string;
  relativePath: string;
  uploadUrl: string;
  uploadMethod: string;
  uploadHeaders: Record<string, string>;
  expiredTime: number;
}

interface UploadCredentialRequest {
  module: AdminImageUploadModule;
  filename: string;
  content_type: string;
}

export interface UploadedImage {
  relativePath: string;
  readUrl: string;
}

const UPLOAD_CREDENTIAL_PATHS = [
  '/v1/admin/media/images/upload-credential',
  '/v1/media/images/upload-credential',
];

let cachedUploadDomain = '';
const INTERNAL_IMAGE_PREFIXES = [
  'task-icons/',
  'task-steps/',
  'home-popup/',
  'support-comments/',
  'support-feedback/',
  'withdrawal-proofs/',
  'task-submissions/',
  'avatars/',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }
  return '';
}

function readNumber(source: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}

function normalizeCredentialResponse(raw: unknown): UploadCredentialResponse {
  const payload = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;
  if (!isRecord(payload)) {
    throw new Error('upload_credential_invalid');
  }

  const domain = readString(payload, 'domain', 'public_domain');
  const objectKey = readString(payload, 'object_key', 'objectKey');
  const relativePath = readString(payload, 'relative_path', 'relativePath') || objectKey;
  const uploadHeaders = isRecord(payload.upload_headers)
    ? Object.fromEntries(
      Object.entries(payload.upload_headers).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
    )
    : {};

  const normalized: UploadCredentialResponse = {
    bucket: readString(payload, 'bucket'),
    region: readString(payload, 'region'),
    domain,
    objectKey,
    relativePath,
    uploadUrl: readString(payload, 'upload_url', 'uploadUrl'),
    uploadMethod: readString(payload, 'upload_method', 'uploadMethod') || 'PUT',
    uploadHeaders,
    expiredTime:
      readNumber(payload, 'expired_time', 'expiredTime')
      || Math.floor(Date.now() / 1000) + 1800,
  };

  if (
    !normalized.objectKey
    || !normalized.relativePath
    || !normalized.uploadUrl
  ) {
    throw new Error('upload_credential_incomplete');
  }

  if (normalized.domain) {
    cachedUploadDomain = normalized.domain;
  }

  return normalized;
}

async function requestUploadCredential(
  payload: UploadCredentialRequest,
): Promise<UploadCredentialResponse> {
  let lastError: unknown = null;

  for (const path of UPLOAD_CREDENTIAL_PATHS) {
    try {
      const response = await post<unknown>(path, {
        module: payload.module,
        filename: payload.filename,
        content_type: payload.content_type,
      });
      return normalizeCredentialResponse(response);
    } catch (error) {
      lastError = error;
      if (isApiError(error) && error.status === 404) {
        continue;
      }
      throw error;
    }
  }

  throw lastError ?? new Error('upload_credential_request_failed');
}

function buildReadUrl(domain: string, objectKey: string): string {
  const trimmedObjectKey = objectKey.trim();
  if (!trimmedObjectKey) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmedObjectKey) || trimmedObjectKey.startsWith('data:image/')) {
    return trimmedObjectKey;
  }
  const trimmedDomain = domain.trim().replace(/\/+$/, '');
  if (!trimmedDomain) {
    return trimmedObjectKey;
  }
  return `${trimmedDomain}/${trimmedObjectKey.replace(/^\/+/, '')}`;
}

async function putObject(
  credential: UploadCredentialResponse,
  file: File,
): Promise<void> {
  const response = await fetch(credential.uploadUrl, {
    method: credential.uploadMethod || 'PUT',
    headers: credential.uploadHeaders,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`upload_failed_${response.status}`);
  }
}

export async function uploadImageToCos(
  file: File,
  module: AdminImageUploadModule,
): Promise<UploadedImage> {
  const credential = await requestUploadCredential({
    module,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
  });
  await putObject(credential, file);

  return {
    relativePath: credential.relativePath,
    readUrl: buildReadUrl(credential.domain, credential.objectKey),
  };
}

export function resolveImagePreviewUrl(
  value: string,
  uploadedImages?: Record<string, string>,
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (uploadedImages?.[trimmed]) {
    return uploadedImages[trimmed];
  }
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  return '';
}

export function resolveAdminImagePreviewUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  const internalPath = normalizeInternalImagePath(trimmed);
  if (!internalPath) {
    return /^https?:\/\//i.test(trimmed) ? trimmed : '';
  }
  const config = buildConfig(import.meta.env);
  return `${config.apiBaseUrl.replace(/\/+$/, '')}/v1/public/media/images/preview?path=${encodeURIComponent(internalPath)}`;
}

export function resolveAdminImageOriginalUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('data:image/') || /^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return resolveAdminImagePreviewUrl(value);
}

export function resolveAdminImageThumbnailUrl(value: string, options: CosThumbnailOptions): string {
  const originalUrl = resolveAdminImageOriginalUrl(value);
  if (!originalUrl || originalUrl.startsWith('data:image/')) {
    return originalUrl;
  }
  if (/\/v1\/public\/media\/images\/preview(?:\?|$)/i.test(originalUrl)) {
    return originalUrl;
  }
  if (/^https?:\/\//i.test(originalUrl)) {
    return buildCosThumbnailUrl(originalUrl, options);
  }
  return originalUrl;
}

export function normalizeImageValueForSubmit(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  const byCachedDomain = cachedUploadDomain ? stripKnownUploadDomain(trimmed, cachedUploadDomain) : '';
  if (byCachedDomain) {
    return byCachedDomain;
  }
  const byKnownPrefix = stripKnownInternalPath(trimmed);
  if (byKnownPrefix) {
    return byKnownPrefix;
  }
  return trimmed;
}

function stripKnownUploadDomain(value: string, domain: string): string {
  try {
    const targetUrl = new URL(value);
    const domainUrl = new URL(domain);
    if (targetUrl.origin !== domainUrl.origin) {
      return '';
    }
    return `${targetUrl.pathname}${targetUrl.search}`.replace(/^\/+/, '');
  } catch {
    return '';
  }
}

function stripKnownInternalPath(value: string): string {
  const directPath = normalizeDirectInternalImagePath(value);
  if (directPath) {
    return directPath;
  }
  try {
    const targetUrl = new URL(value);
    return normalizeDirectInternalImagePath(targetUrl.pathname.replace(/^\/+/, ''));
  } catch {
    return '';
  }
}

function normalizeInternalImagePath(value: string): string {
  const byDirectPath = normalizeDirectInternalImagePath(value);
  if (byDirectPath) {
    return byDirectPath;
  }
  try {
    const targetUrl = new URL(value);
    return normalizeDirectInternalImagePath(targetUrl.pathname.replace(/^\/+/, ''));
  } catch {
    return '';
  }
}

function normalizeDirectInternalImagePath(value: string): string {
  const normalized = value.trim().replace(/^\/+/, '');
  if (!normalized || normalized.includes('\\') || normalized.includes('\0') || normalized.includes('?') || normalized.includes('#')) {
    return '';
  }
  if (normalized.split('/').some((part) => !part || part === '.' || part === '..')) {
    return '';
  }
  const lower = normalized.toLowerCase();
  for (const prefix of INTERNAL_IMAGE_PREFIXES) {
    if (lower.startsWith(prefix)) {
      return normalized;
    }
  }
  return '';
}
